import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../../components/ui/badge';
import { DollarSign, Plus, CheckCircle, Send, Eye } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';

const LoansManagement = () => {
  const [loans, setLoans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const { toast } = useToast();

  const [offerData, setOfferData] = useState({
    account_id: '',
    principal: 10000,
    interest_rate_monthly: 0.0299,
    term_months: 12
  });

  useEffect(() => {
    fetchLoans();
    fetchCustomers();
  }, [statusFilter]);

  const fetchLoans = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/admin/loans${params}`);
      setLoans(response.data);
    } catch (error) {
      console.error('Error fetching loans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/admin/customers');
      setCustomers(response.data.filter(c => c.status === 'ACTIVE'));
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCreateOffer = async () => {
    if (!offerData.account_id) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Selecione um cliente',
      });
      return;
    }

    try {
      await api.post('/admin/loans/offer', offerData);
      toast({
        title: 'Oferta criada!',
        description: 'Oferta de empréstimo criada com sucesso.',
      });
      setOfferDialogOpen(false);
      setOfferData({
        account_id: '',
        principal: 10000,
        interest_rate_monthly: 0.0299,
        term_months: 12
      });
      fetchLoans();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao criar oferta',
      });
    }
  };

  const handleApproveLoan = async (loanId) => {
    try {
      await api.post(`/admin/loans/${loanId}/approve`);
      toast({
        title: 'Empréstimo aprovado!',
        description: 'Empréstimo aprovado com sucesso.',
      });
      fetchLoans();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao aprovar',
      });
    }
  };

  const handleDisburseLoan = async (loanId) => {
    try {
      await api.post(`/admin/loans/${loanId}/disburse`);
      toast({
        title: 'Empréstimo desembolsado!',
        description: 'Valor creditado e parcelas criadas.',
      });
      fetchLoans();
      if (selectedLoan?.id === loanId) {
        fetchLoanDetail(loanId);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao desembolsar',
      });
    }
  };

  const fetchLoanDetail = async (loanId) => {
    try {
      const response = await api.get(`/admin/loans/${loanId}/detail`);
      setSelectedLoan(response.data);
      setDetailSheetOpen(true);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao buscar detalhes',
      });
    }
  };

  const getAccountIdForCustomer = async (customerId) => {
    try {
      const response = await api.get(`/admin/customers/${customerId}/account`);
      return response.data.id;
    } catch (error) {
      return null;
    }
  };

  const handleCustomerSelect = async (customerId) => {
    const accountId = await getAccountIdForCustomer(customerId);
    if (accountId) {
      setOfferData({ ...offerData, account_id: accountId });
    } else {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Cliente não possui conta',
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const config = {
      OFFERED: { variant: 'secondary', label: 'Ofertado' },
      APPROVED: { variant: 'default', label: 'Aprovado' },
      DISBURSED: { variant: 'default', label: 'Desembolsado' },
      ACTIVE: { variant: 'default', label: 'Ativo' },
      CLOSED: { variant: 'outline', label: 'Fechado' },
      DEFAULTED: { variant: 'destructive', label: 'Inadimplente' },
    };
    const { variant, label } = config[status] || { variant: 'outline', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="loans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Gestão de Empréstimos</h1>
          <p className="text-muted-foreground mt-2">Ofertas, aprovações e desembolsos</p>
        </div>
        <Dialog open={offerDialogOpen} onOpenChange={setOfferDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Nova Oferta
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar Oferta de Empréstimo</DialogTitle>
              <DialogDescription>Defina os termos do empréstimo</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select onValueChange={handleCustomerSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id.toString()}>
                        {customer.name} - {customer.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor Principal</Label>
                <Input
                  type="number"
                  value={offerData.principal}
                  onChange={(e) => setOfferData({ ...offerData, principal: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Taxa de Juros Mensal (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={(offerData.interest_rate_monthly * 100).toFixed(2)}
                  onChange={(e) => setOfferData({ ...offerData, interest_rate_monthly: parseFloat(e.target.value) / 100 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Prazo (meses)</Label>
                <Input
                  type="number"
                  value={offerData.term_months}
                  onChange={(e) => setOfferData({ ...offerData, term_months: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOfferDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateOffer}>Criar Oferta</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter || 'ALL'} onValueChange={(val) => setStatusFilter(val === 'ALL' ? '' : val)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="OFFERED">Ofertado</SelectItem>
                  <SelectItem value="APPROVED">Aprovado</SelectItem>
                  <SelectItem value="DISBURSED">Desembolsado</SelectItem>
                  <SelectItem value="ACTIVE">Ativo</SelectItem>
                  <SelectItem value="CLOSED">Fechado</SelectItem>
                  <SelectItem value="DEFAULTED">Inadimplente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loans List */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle>Lista de Empréstimos</CardTitle>
          <CardDescription>{loans.length} empréstimo(s) encontrado(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            </div>
          ) : loans.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              Nenhum empréstimo encontrado
            </div>
          ) : (
            <div className="space-y-3">
              {loans.map((loan) => (
                <div
                  key={loan.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">Conta #{loan.account_id}</p>
                      {getStatusBadge(loan.status)}
                    </div>
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <span>Valor: {formatCurrency(loan.principal)}</span>
                      <span>Taxa: {(loan.interest_rate_monthly * 100).toFixed(2)}%</span>
                      <span>Prazo: {loan.term_months}m</span>
                      <span>Criado: {formatDate(loan.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {loan.status === 'OFFERED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                        onClick={() => handleApproveLoan(loan.id)}
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Aprovar
                      </Button>
                    )}
                    {(loan.status === 'APPROVED' || loan.status === 'OFFERED') && (
                      <Button
                        size="sm"
                        className="rounded-full"
                        onClick={() => handleDisburseLoan(loan.id)}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        Desembolsar
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => fetchLoanDetail(loan.id)}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Detalhes
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loan Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedLoan && (
            <>
              <SheetHeader>
                <SheetTitle>Detalhes do Empréstimo #{selectedLoan.id}</SheetTitle>
                <SheetDescription>
                  Status: {getStatusBadge(selectedLoan.status)}
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Valor Principal</p>
                    <p className="text-lg font-bold font-mono">{formatCurrency(selectedLoan.principal)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Taxa Mensal</p>
                    <p className="text-lg font-bold font-mono">{(selectedLoan.interest_rate_monthly * 100).toFixed(2)}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Prazo</p>
                    <p className="text-lg font-bold">{selectedLoan.term_months} meses</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Conta</p>
                    <p className="text-lg font-bold">#{selectedLoan.account_id}</p>
                  </div>
                </div>

                {selectedLoan.disbursed_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Desembolsado em</p>
                    <p className="font-medium">{formatDate(selectedLoan.disbursed_at)}</p>
                  </div>
                )}

                {selectedLoan.installments && selectedLoan.installments.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold">Parcelas ({selectedLoan.installments.length})</h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {selectedLoan.installments.map((inst) => (
                        <div
                          key={inst.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-border/50"
                        >
                          <div>
                            <p className="font-medium">Parcela {inst.number}</p>
                            <p className="text-sm text-muted-foreground">
                              Vencimento: {formatDate(inst.due_date)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold">{formatCurrency(inst.amount_total)}</p>
                            <Badge variant={inst.status === 'PAID' ? 'default' : inst.status === 'OVERDUE' ? 'destructive' : 'secondary'}>
                              {inst.status === 'PAID' ? 'Pago' : inst.status === 'OVERDUE' ? 'Vencido' : 'Aberto'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default LoansManagement;